
import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Container } from '@/components/ui/container';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Slider } from '@/components/ui/slider';
import { Checkbox } from '@/components/ui/checkbox';
import { Loader2, Search, SlidersHorizontal, X } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { ProductCard } from '@/components/product/card';
import { useQuery } from '@tanstack/react-query';
import CitySelect from '@/components/ui/CitySelect';
import { extractCategoryId } from '@/utils/slugUtils';
import { 
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
  SheetClose
} from '@/components/ui/sheet';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { CheckedState } from '@radix-ui/react-checkbox';

const Category = () => {
  const { idSlug } = useParams<{ idSlug: string }>();
  const [searchTerm, setSearchTerm] = useState('');
  const [priceRange, setPriceRange] = useState<[number, number]>([0, 1000]);
  const [location, setLocation] = useState<string>('');
  const [onlyPremium, setOnlyPremium] = useState(false);
  const [sortBy, setSortBy] = useState<'newest' | 'price_asc' | 'price_desc'>('newest');
  const [isMobileFiltersOpen, setIsMobileFiltersOpen] = useState(false);
  const [selectedSubcategory, setSelectedSubcategory] = useState<string>('');
  
  // Extract category ID from idSlug parameter
  const categoryId = idSlug ? extractCategoryId(idSlug) : null;
  
  // Fetch category details by ID
  const { data: category, isLoading: categoryLoading } = useQuery({
    queryKey: ['category', categoryId],
    queryFn: async () => {
      if (!categoryId) throw new Error('No category ID provided');
      
      const { data, error } = await supabase
        .from('categories')
        .select('*')
        .eq('id', parseInt(categoryId, 10))
        .single();
      
      if (error) throw error;
      return data;
    },
    enabled: !!categoryId,
  });

  // Fetch subcategories for the current category
  const { data: subcategories = [], isLoading: subcategoriesLoading } = useQuery({
    queryKey: ['subcategories', category?.id],
    queryFn: async () => {
      if (!category?.id) return [];
      
      const { data, error } = await supabase
        .from('subcategories')
        .select('*')
        .eq('parent_id', category.id)
        .order('name');
      
      if (error) throw error;
      return data || [];
    },
    enabled: !!category?.id,
  });
  
  // Fetch listings for this category with filters
  const { data: listings = [], isLoading: listingsLoading } = useQuery({
    queryKey: ['categoryListings', category?.id, searchTerm, priceRange, location, onlyPremium, sortBy, selectedSubcategory],
    queryFn: async () => {
      if (!category?.id) return [];
      
      let query = supabase
        .from('listings')
        .select('*')
        .eq('category_id', category.id)
        .eq('is_sold', false)
        .eq('is_deleted', false)
        .eq('is_active', true)
        .gte('price', priceRange[0])
        .lte('price', priceRange[1]);
      
      if (searchTerm) {
        query = query.ilike('title', `%${searchTerm}%`);
      }
      
      if (location) {
        query = query.eq('location', location);
      }
      
      if (onlyPremium) {
        query = query.eq('is_premium', true);
      }

      if (selectedSubcategory) {
        const subcategoryId = parseInt(selectedSubcategory, 10);
        if (!isNaN(subcategoryId)) {
          query = query.eq('subcategory_id', subcategoryId);
        }
      }
      
      // Apply sorting
      switch (sortBy) {
        case 'newest':
          query = query.order('is_premium', { ascending: false }).order('created_at', { ascending: false });
          break;
        case 'price_asc':
          query = query.order('is_premium', { ascending: false }).order('price', { ascending: true });
          break;
        case 'price_desc':
          query = query.order('is_premium', { ascending: false }).order('price', { ascending: false });
          break;
      }
      
      const { data, error } = await query;
      
      if (error) throw error;
      return data;
    },
    enabled: !!category?.id,
  });
  
  // Reset filters
  const resetFilters = () => {
    setSearchTerm('');
    setPriceRange([0, 1000]);
    setLocation('');
    setOnlyPremium(false);
    setSortBy('newest');
    setSelectedSubcategory('');
  };

  // Check if any filter is active
  const isAnyFilterActive = () => {
    return (
      searchTerm !== '' || 
      location !== '' || 
      priceRange[0] > 0 || 
      priceRange[1] < 1000 || 
      onlyPremium || 
      sortBy !== 'newest' ||
      selectedSubcategory !== ''
    );
  };

  // Handle subcategory selection
  const handleSubcategoryChange = (subcategoryId: string) => {
    setSelectedSubcategory(subcategoryId === selectedSubcategory ? '' : subcategoryId);
  };
  
  // Handlers with correct typing
  const handlePriceRangeChange = (value: number[]) => {
    setPriceRange([value[0], value[1]]);
  };
  
  const handleCheckboxChange = (checked: CheckedState) => {
    setOnlyPremium(checked === true);
  };
  
  return (
    <Container>
      <div className="py-8">
        {categoryLoading ? (
          <div className="flex justify-center items-center h-32">
            <Loader2 className="h-8 w-8 animate-spin text-brand-500" />
          </div>
        ) : (
          <h1 className="text-3xl font-bold mb-4">{category?.name || 'Category'}</h1>
        )}
        
        {/* Filter actions bar */}
        <div className="flex flex-wrap items-center justify-between gap-2 mb-6">
          <div className="flex items-center gap-2 flex-wrap flex-1">
            <div className="relative flex-1 max-w-md">
              <Input
                placeholder="Search in this category..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            </div>
            
            {/* Display applied filters as badges */}
            {isAnyFilterActive() && (
              <div className="flex flex-wrap gap-2 items-center">
                {location && (
                  <Badge variant="outline" className="px-3 py-1 bg-gray-100">
                    {location}
                    <button onClick={() => setLocation('')} className="ml-2">
                      <X className="h-3 w-3" />
                    </button>
                  </Badge>
                )}
                
                {(priceRange[0] > 0 || priceRange[1] < 1000) && (
                  <Badge variant="outline" className="px-3 py-1 bg-gray-100">
                    {priceRange[0]}€ - {priceRange[1]}€
                    <button onClick={() => setPriceRange([0, 1000])} className="ml-2">
                      <X className="h-3 w-3" />
                    </button>
                  </Badge>
                )}
                
                {selectedSubcategory && (
                  <Badge variant="outline" className="px-3 py-1 bg-gray-100">
                    {subcategories.find(sc => sc.id.toString() === selectedSubcategory)?.name}
                    <button onClick={() => setSelectedSubcategory('')} className="ml-2">
                      <X className="h-3 w-3" />
                    </button>
                  </Badge>
                )}
                
                {onlyPremium && (
                  <Badge variant="outline" className="px-3 py-1 bg-gray-100">
                    Premium
                    <button onClick={() => setOnlyPremium(false)} className="ml-2">
                      <X className="h-3 w-3" />
                    </button>
                  </Badge>
                )}
                
                <Button variant="ghost" size="sm" onClick={resetFilters} className="text-sm">
                  Clear all
                </Button>
              </div>
            )}
          </div>
          
          <div className="flex gap-2">
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value as 'newest' | 'price_asc' | 'price_desc')}
              className="py-2 px-4 border rounded-md text-sm"
            >
              <option value="newest">Newest</option>
              <option value="price_asc">Price: Low to High</option>
              <option value="price_desc">Price: High to Low</option>
            </select>
            
            <Sheet>
              <SheetTrigger asChild>
                <Button variant="outline" size="icon" className="md:hidden">
                  <SlidersHorizontal className="h-4 w-4" />
                </Button>
              </SheetTrigger>
              <SheetContent side="left">
                <SheetHeader>
                  <SheetTitle>Filters</SheetTitle>
                </SheetHeader>
                <div className="py-4">
                  {/* Mobile filters */}
                  <div className="space-y-6">
                    {/* Subcategories */}
                    {subcategories.length > 0 && (
                      <div>
                        <h3 className="font-medium mb-3">Subcategories</h3>
                        <div className="space-y-2">
                          {subcategories.map((subcategory) => (
                            <div key={subcategory.id} className="flex items-center">
                              <Checkbox
                                id={`subcategory-${subcategory.id}-mobile`}
                                checked={selectedSubcategory === subcategory.id.toString()}
                                onCheckedChange={() => handleSubcategoryChange(subcategory.id.toString())}
                              />
                              <Label
                                htmlFor={`subcategory-${subcategory.id}-mobile`}
                                className="ml-2 text-sm font-normal cursor-pointer"
                              >
                                {subcategory.name}
                              </Label>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    {/* Price Range */}
                    <div>
                      <h3 className="font-medium mb-3">Price Range</h3>
                      <div className="space-y-3">
                        <Slider
                          min={0}
                          max={1000}
                          step={10}
                          value={priceRange}
                          onValueChange={handlePriceRangeChange}
                          className="my-4"
                        />
                        <div className="flex items-center justify-between">
                          <div className="border rounded-md p-2 w-24">
                            <p className="text-xs text-gray-500">Min</p>
                            <p>{priceRange[0]}€</p>
                          </div>
                          <div className="border rounded-md p-2 w-24">
                            <p className="text-xs text-gray-500">Max</p>
                            <p>{priceRange[1]}€</p>
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    {/* Location */}
                    <div>
                      <h3 className="font-medium mb-3">Location</h3>
                      <CitySelect
                        value={location}
                        onChange={(value) => setLocation(value)}
                      />
                    </div>
                    
                    {/* Premium only */}
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="premium-mobile"
                        checked={onlyPremium}
                        onCheckedChange={handleCheckboxChange}
                      />
                      <Label
                        htmlFor="premium-mobile"
                        className="text-sm font-normal cursor-pointer"
                      >
                        Show premium listings only
                      </Label>
                    </div>
                  </div>
                  
                  <div className="flex gap-2 mt-6">
                    <Button onClick={resetFilters} variant="outline" className="w-1/2">
                      Reset
                    </Button>
                    <SheetClose asChild>
                      <Button className="w-1/2">
                        Apply
                      </Button>
                    </SheetClose>
                  </div>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
        
        {/* Main content area with filters and products */}
        <div className="flex flex-col md:flex-row gap-6">
          {/* Desktop filters sidebar */}
          <div className="hidden md:block w-64 shrink-0">
            <div className="border rounded-lg p-4 sticky top-24">
              <h2 className="font-medium text-lg mb-4">Filters</h2>
              <div className="space-y-6">
                {/* Subcategories */}
                {subcategories.length > 0 && (
                  <div>
                    <h3 className="font-medium mb-3">Subcategories</h3>
                    <ScrollArea className="h-[180px] pr-4">
                      <div className="space-y-2">
                        {subcategories.map((subcategory) => (
                          <div key={subcategory.id} className="flex items-center">
                            <Checkbox
                              id={`subcategory-${subcategory.id}`}
                              checked={selectedSubcategory === subcategory.id.toString()}
                              onCheckedChange={() => handleSubcategoryChange(subcategory.id.toString())}
                            />
                            <Label
                              htmlFor={`subcategory-${subcategory.id}`}
                              className="ml-2 text-sm font-normal cursor-pointer"
                            >
                              {subcategory.name}
                            </Label>
                          </div>
                        ))}
                      </div>
                    </ScrollArea>
                  </div>
                )}
                
                {/* Price Range */}
                <div>
                  <h3 className="font-medium mb-3">Price Range</h3>
                  <div className="space-y-3">
                    <Slider
                      min={0}
                      max={1000}
                      step={10}
                      value={priceRange}
                      onValueChange={handlePriceRangeChange}
                      className="my-4"
                    />
                    <div className="flex items-center justify-between">
                      <div className="border rounded-md p-2 w-20">
                        <p className="text-xs text-gray-500">Min</p>
                        <p>{priceRange[0]}€</p>
                      </div>
                      <div className="border rounded-md p-2 w-20">
                        <p className="text-xs text-gray-500">Max</p>
                        <p>{priceRange[1]}€</p>
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Location */}
                <div>
                  <h3 className="font-medium mb-3">Location</h3>
                  <CitySelect
                    value={location}
                    onChange={(value) => setLocation(value)}
                  />
                </div>
                
                {/* Premium only */}
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="premium"
                    checked={onlyPremium}
                    onCheckedChange={handleCheckboxChange}
                  />
                  <Label
                    htmlFor="premium"
                    className="text-sm font-normal cursor-pointer"
                  >
                    Show premium listings only
                  </Label>
                </div>
                
                <Button onClick={resetFilters} variant="outline" className="w-full">
                  Reset Filters
                </Button>
              </div>
            </div>
          </div>
          
          {/* Products grid */}
          <div className="flex-1">
            {listingsLoading ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {[1, 2, 3, 4, 5, 6].map((item) => (
                  <div key={item} className="border rounded-lg p-4 h-80 animate-pulse">
                    <div className="bg-gray-200 h-40 rounded-md mb-4"></div>
                    <div className="bg-gray-200 h-4 rounded-md w-3/4 mb-2"></div>
                    <div className="bg-gray-200 h-4 rounded-md w-1/2 mb-4"></div>
                    <div className="bg-gray-200 h-8 rounded-md w-1/3"></div>
                  </div>
                ))}
              </div>
            ) : listings.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {listings.map((listing) => (
                  <ProductCard
                    key={listing.id}
                    id={String(listing.id)}
                    title={listing.title}
                    price={Number(listing.price)}
                    location={listing.location}
                    imageUrl={listing.images?.[0]}
                    category={category?.name}
                    createdAt={listing.created_at}
                    condition={listing.condition}
                    isPremium={listing.is_premium}
                    stock={listing.stock}
                  />
                ))}
              </div>
            ) : (
              <div className="text-center py-16">
                <h3 className="text-xl font-medium mb-2">No listings found</h3>
                <p className="text-gray-500 mb-4">Try adjusting your filters or check back later</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </Container>
  );
};

export default Category;
